import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  type ElementRef,
  inject,
  signal,
  viewChild
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatFormField, MatHint, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { translate, TranslocoDirective } from '@jsverse/transloco';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { EMPTY, of } from 'rxjs';
import { catchError, filter, finalize, switchMap } from 'rxjs/operators';

import { ApiService } from '../../core/api/api.service';
import { CurrentUserService, UploadsService, type UploadTask } from '../../core/services';
import { ConfirmService } from '../confirm';
import { MaxLengthDirective } from '../directives';
import { ModalHeaderComponent } from '../modal-header/modal-header.component';
import { SnackBarService } from '../snack-bar';
import { SupportAttachmentComponent } from './support-attachment/support-attachment.component';
import { type SupportFile } from './support-file';

@Component({
  templateUrl: './support-request.component.html',
  styleUrl: './support-request.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatHint,
    MatInput,
    MatProgressSpinner,
    TranslocoDirective,
    MaxLengthDirective,
    ModalHeaderComponent,
    SupportAttachmentComponent
  ],
  providers: [ConfirmService, SnackBarService]
})
export class SupportRequestComponent {
  readonly #activeModal = inject(NgbActiveModal);
  readonly #api = inject(ApiService);
  readonly #currentUser = inject(CurrentUserService);
  readonly #fb = inject(FormBuilder);
  readonly #confirm = inject(ConfirmService);
  readonly #snackBar = inject(SnackBarService);
  readonly #uploadsService = inject(UploadsService);
  readonly #dr = inject(DestroyRef);

  protected readonly fileInput = viewChild.required<ElementRef<HTMLInputElement>>('fileInput');
  #submitted = false;

  public readonly isSubmitting = signal(false);
  readonly #pendingSubmit = signal(false);
  readonly #hasUploadingFiles = computed(() => this.attachments().some(a => !a.file));

  public readonly maxMessageLength = 500;
  readonly #maxTotalSizeBytes = 10 * 1024 * 1024;

  public isDragOver = false;
  public readonly attachments = signal<SupportFile[]>([]);
  public readonly isSizeExceeded = computed(
    () => this.attachments().reduce((sum, a) => sum + (a.task?.originalFile?.size ?? 0), 0) > this.#maxTotalSizeBytes
  );

  public readonly form = this.#fb.group({
    name: ['', [Validators.required]],
    email: ['', [Validators.required, Validators.email]],
    message: ['', [Validators.required]]
  });

  constructor() {
    const profile = this.#currentUser.profile;
    if (profile) {
      this.form.patchValue({
        name: `${profile.firstName} ${profile.lastName}`.trim(),
        email: profile.email ?? ''
      });
    }

    effect(() => {
      if (this.isSubmitting()) {
        this.form.disable();
      } else {
        this.form.enable();
      }
    });

    effect(() => {
      if (this.#pendingSubmit() && !this.#hasUploadingFiles()) {
        this.#pendingSubmit.set(false);
        this.#sendForm();
      }
    });

    this.#dr.onDestroy(() => {
      if (!this.#submitted) {
        this.#cleanupAttachments();
      }
    });
  }

  public get messageLength(): number {
    return (this.form.controls.message.value ?? '').length;
  }

  public close(): void {
    this.#activeModal.dismiss();
  }

  public submit(): void {
    if (this.form.invalid || this.isSizeExceeded()) {
      this.form.markAllAsTouched();
      return;
    }

    this.isSubmitting.set(true);

    if (this.#hasUploadingFiles()) {
      this.#pendingSubmit.set(true);
      return;
    }

    this.#sendForm();
  }

  #sendForm(): void {
    const { name, email, message } = this.form.value;

    const attachments = this.attachments()
      .filter(a => a.file)
      .map(a => ({ fileName: a.file!.fileName, url: a.file!.url }));

    this.#api.auth
      .sendForm({
        name: name!,
        email: email!,
        message: message!,
        ...(attachments.length ? { attachments } : {})
      })
      .pipe(
        catchError(() => {
          this.#snackBar.open({ icon: '', title: translate<string>('supportRequestError') });
          return EMPTY;
        }),
        finalize(() => this.isSubmitting.set(false)),
        takeUntilDestroyed(this.#dr)
      )
      .subscribe(() => {
        this.#submitted = true;
        this.#snackBar.open(
          { icon: 'sent-success', title: translate<string>('supportRequestSent') },
          { panelClass: 'container-flexible', duration: 60000 }
        );
        this.#activeModal.close();
      });
  }

  public openFilePicker(): void {
    this.fileInput().nativeElement.click();
  }

  public onFileInputChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files?.length) {
      this.addFiles(input.files);
      input.value = '';
    }
  }

  public onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = true;
  }

  public onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
  }

  public onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
    if (!this.isSubmitting() && event.dataTransfer?.files?.length) {
      this.addFiles(event.dataTransfer.files);
    }
  }

  public addFiles(files: FileList): void {
    for (const file of Array.from(files)) {
      const attachment: SupportFile = { task: this.#uploadsService.upload(file) };
      this.attachments.update(arr => [...arr, attachment]);

      this.#uploadsService
        .getFileInfoFromTask(attachment.task as UploadTask)
        .pipe(takeUntilDestroyed(this.#dr))
        .subscribe(info => {
          this.attachments.update(arr => {
            const index = arr.findIndex(a => a === attachment);
            if (index === -1) {
              return arr;
            }
            const updated = [...arr];
            updated[index] = {
              task: attachment.task,
              file: { fileName: info.fileName, url: info.url, path: info.path ?? '' }
            };
            return updated;
          });
        });
    }
  }

  public removeAttachment(attachment: SupportFile): void {
    const fileName = attachment.file?.fileName || this.#uploadsService.getOriginalFileName(attachment.task) || '';
    this.#confirm
      .open({
        label: 'deleteAttachment',
        message: 'confirmDelete',
        item: fileName
      })
      .pipe(
        filter(Boolean),
        switchMap(() => {
          const current = this.attachments().find(a => a.task === attachment.task) ?? attachment;
          this.attachments.update(arr => arr.filter(a => a.task !== attachment.task));
          if (!current.file) {
            current.task?.cancel();
            return of(void 0);
          }
          return this.#uploadsService.deleteFile(current.file.path);
        }),
        takeUntilDestroyed(this.#dr)
      )
      .subscribe();
  }

  #cleanupAttachments(): void {
    for (const attachment of this.attachments()) {
      attachment.task?.cancel();
      const path = attachment.file?.path;
      if (path) {
        this.#uploadsService.deleteFile(path).subscribe();
      }
    }
  }
}
